import React, { useState } from 'react';
import { Box, TextField, Button, Typography, InputAdornment, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import coverPic from "../../assets/images/Hrms.png";
import Loader from '../../common/spinner';
import { postData, putData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';

const ForgotPasswordPage = () => {
    // Steps: 1 (Email), 2 (OTP), 3 (Reset)
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [otpInput, setOtpInput] = useState('');
    const [passwords, setPasswords] = useState({ new: '', confirm: '' });
    const [showPass, setShowPass] = useState({ new: false, confirm: false });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleError = (err) => {
        const msg = err.response?.data?.message || err.message || "An error occurred";
        setError(err.response?.status === 404 ? "User not found with this email." : msg);
    };

    const handleGenerateOtp = async () => {
        if (!email.includes('@') || !email.includes('.')) return setError("Enter a valid email.");
        setIsLoading(true); setError('');
        try {
            await postData(API_ENDPOINTS.generateVerificationOtp, { email });
            setStep(2);
        } catch (err) { handleError(err); }
        finally { setIsLoading(false); }
    };

    const handleVerifyOTP = async () => {
        setIsLoading(true); setError('');
        try {
            await postData(API_ENDPOINTS.verifyOtp, { email, otp: otpInput });
            setStep(3);
        } catch (err) { handleError(err); }
        finally { setIsLoading(false); }
    };

    const handleChangePassword = async () => {
        if (passwords.new !== passwords.confirm) return setError("Passwords do not match.");
        if (passwords.new.length < 6) return setError("Password must be at least 6 characters.");
        setIsLoading(true); setError('');
        try {
            await putData(API_ENDPOINTS.resetUserPassword, { email, newPassword: passwords.new, confirmPassword: passwords.confirm });
            navigate('/login');
        } catch (err) { handleError(err); }
        finally { setIsLoading(false); }
    };

    const renderField = (placeholder, value, onChange, type = 'text', toggleKey = null) => (
        <TextField
            fullWidth size="small" placeholder={placeholder} type={type}
            value={value} onChange={(e) => onChange(e.target.value)}
            sx={{ backgroundColor: '#fff', borderRadius: '4px', mb: 2 }}
            InputProps={{
                style: { color: '#000' },
                endAdornment: toggleKey && (
                    <InputAdornment position="end">
                        <IconButton onClick={() => setShowPass(s => ({ ...s, [toggleKey]: !s[toggleKey] }))}>
                            {showPass[toggleKey] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                )
            }}
        />
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100vh', width: '100vw', bgcolor: '#292929' }}>
            {isLoading && <Loader />}
            <Box sx={{ flex: 1, backgroundImage: `url(${coverPic})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <Box sx={{ width: '100%', maxWidth: '350px', textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#fff' }}>Forgot Password?</Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: '#bbb' }}>
                        {step === 1 && "Enter your email to receive an OTP."}
                        {step === 2 && "Enter the OTP sent to your registered email."}
                        {step === 3 && "Create a new password to secure your account."}
                    </Typography>

                    {step === 1 && renderField("Enter email address", email, setEmail)}
                    {step === 2 && renderField("Enter OTP", otpInput, setOtpInput)}
                    {step === 3 && (
                        <>
                            {renderField("Email address", email, setEmail)}
                            {renderField("New password", passwords.new, (v) => setPasswords(p => ({ ...p, new: v })), showPass.new ? 'text' : 'password', 'new')}
                            {renderField("Confirm password", passwords.confirm, (v) => setPasswords(p => ({ ...p, confirm: v })), showPass.confirm ? 'text' : 'password', 'confirm')}
                        </>
                    )}

                    {error && <Typography color="error" sx={{ mb: 2, fontSize: '0.85rem', bgcolor: 'rgba(255,0,0,0.1)', p: 1, borderRadius: 1 }}>{error}</Typography>}

                    <Button
                        fullWidth variant="contained"
                        onClick={step === 1 ? handleGenerateOtp : step === 2 ? handleVerifyOTP : handleChangePassword}
                        sx={{ bgcolor: '#1a237e', color: '#fff', p: 1.5, fontWeight: 'bold', '&:hover': { bgcolor: '#0d47a1' } }}
                    >
                        {step === 1 ? "Generate OTP" : step === 2 ? "Verify OTP" : "Change Password"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default ForgotPasswordPage;